import { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

import paymentOptions from "../mockdata/paymentOptions.json";
import { FormattedMessage } from "react-intl";
import CONSTANTS from "../utils/constants";
import { PBLResponseContext, UUIDContext } from "../UUIDContexted";
import GeneralErrorWidget from "./GeneralErrorWidget";
import { usePaymentForm } from "../context/PaymentFormContext";
import DirectDebitIcon from "../assets/img/payment-methods/DirectDebitIcon.svg";
import DirectDebitWhiteIcon from "../assets/img/payment-methods/DirectDebitWhiteIcon.svg";
import secured_installement_icon from "../assets/img/payment-methods/secured_installement_icon.svg";
import secured_installement_white_icon from "../assets/img/payment-methods/secured_installement_white_icon.svg";
import SecuredInvoiceIcon from "../assets/img/payment-methods/secured_invoice_icon.svg";
import SecuredInvoiceWhiteIcon from "../assets/img/payment-methods/secured_invoice_white_icon.svg";
import paypalIcon from "../assets/img/payment-methods/paypalIcon.svg";
import creditCardIcon from "../assets/img/payment-methods/creditCardIcon.svg";
import CreditCardWhiteIcon from "../assets/img/payment-methods/CreditCardWhiteIcon.svg";
import amexLogo from "../assets/img/scheme-logos/amex.svg";
import dinersLogo from "../assets/img/scheme-logos/diners.svg";
import jcbLogo from "../assets/img/scheme-logos/jcb.svg";
import mastercardLogo from "../assets/img/scheme-logos/mastercard.svg";
import visaLogo from "../assets/img/scheme-logos/visa.svg";
import visaLogoWhite from "../assets/img/scheme-logos/visa_white.svg";
import mastercardLogoWhite from "../assets/img/scheme-logos/mastercard.svg";
import dinersLogoWhite from "../assets/img/scheme-logos/diners_white.svg";
import clickToPayLogoWhite from "../assets/img/scheme-logos/Click-to-Pay-light.svg";
import clickToPayLogoDark from "../assets/img/scheme-logos/Click-to-Pay-dark.svg";
import weroLogo from "../assets/img/payment-methods/wero-logo.png";
import googlePayIcon from "../assets/img/payment-methods/Google-Pay.svg";

import "./paymentMethods.css";
interface ButtonComponentProps {
  choosenMethod?: string;
}

interface PaymentOptions {
  id: string;
  label: string;
  paymentMethodsID: string;
  translationKey: string;
}

interface SchemeLogo {
  [schemeId: string]: string;
}

const usePaymentIcons = (theme: string) => {
  const iconMapping: Record<string, string> = {
    "3392": theme === "dark" ? DirectDebitWhiteIcon : DirectDebitIcon,
    "3391": theme === "dark" ? secured_installement_white_icon : secured_installement_icon,
    "3390": theme === "dark" ? SecuredInvoiceWhiteIcon : SecuredInvoiceIcon,
    "900": weroLogo,
    "840": paypalIcon,
    "320": googlePayIcon,
    "1,2,3,125,132,5002": theme === "dark" ? CreditCardWhiteIcon : creditCardIcon,
  };

  const schemeLogoMapping: SchemeLogo[] = [
    { "5002": theme === "dark" ? clickToPayLogoDark : clickToPayLogoWhite },
    { "1": theme === "dark" ? visaLogoWhite : visaLogo },
    { "2": amexLogo },
    { "3": theme === "dark" ? mastercardLogoWhite : mastercardLogo },
    { "125": jcbLogo },
    { "132": theme === "dark" ? dinersLogoWhite : dinersLogo }
  ];

  return useMemo(() => ({
    iconMapping,
    schemeLogoMapping
  }), [theme]);
}

const PaymentMethods: React.FC<ButtonComponentProps> = ({ choosenMethod }) => {
  const { theme } = useTheme();
  const { PaymentFormData, setPaymentFormData } = usePaymentForm();
  const { iconMapping, schemeLogoMapping } = usePaymentIcons(theme);

  const PBLData = useContext(UUIDContext);
  const { pblResponseData } = useContext(PBLResponseContext);

  const { uuid } = useParams();
  const navigate = useNavigate();

  const [selectedMethod, setSelectedMethod] = useState(choosenMethod);
  const [dynamicPaymentOptions, setDynamicPaymentOptions] = useState<PaymentOptions[]>(paymentOptions);
  const [schemeLogos, setSchemeLogos] = useState<SchemeLogo[]>([]);
  const [isEditingPaymentMethod, setIsEditPaymentMethod] = useState(false);
  const [isMethodchanged, setMethodChanged] = useState(false);

  const creditCardScemeIds = ["1", "2", "3", "125", "132", "5002"];
  const isOverviewPage = PaymentFormData.isOverviewPage;

  useEffect(() => {
    if (!(PBLData && PBLData.paymentMethods)) return;

    const dynamicPaymentOptions = paymentOptions.filter((option) => {
      if (option.id === "creditCard" && PBLData.token) {
        const availableCreditCards = option.paymentMethodsID.split(",");
        return PBLData.paymentMethods.some(card => availableCreditCards.includes(card));
      }
      return PBLData.paymentMethods.includes(option.paymentMethodsID);
    });

    const finalOptions = PaymentFormData.isOverviewPage && PaymentFormData.paymentMethod && !isEditingPaymentMethod
      ? dynamicPaymentOptions.filter(option => option.paymentMethodsID === PaymentFormData.paymentMethod)
      : dynamicPaymentOptions;

    setDynamicPaymentOptions(finalOptions);
  }, [PBLData, PaymentFormData.isOverviewPage, isEditingPaymentMethod]);

  useEffect(() => {
    if (!PBLData?.paymentMethods) return;

    const isCreditCardAvailable = PBLData.paymentMethods.some(method => creditCardScemeIds.includes(method));

    if (isCreditCardAvailable) {
      setSchemeLogos(
        schemeLogoMapping.filter((logo: {}) => PBLData.paymentMethods.includes(Object.keys(logo)[0]))
      )
    };
  }, [PBLData, theme]);

  const handleMethodSelect = (methodId: string) => {
    const selected = paymentOptions.find(opt => opt.id === methodId);
    if (!selected) return;

    const methodChanged = selected.paymentMethodsID !== PaymentFormData.paymentMethod;

    if (methodChanged) {
      setMethodChanged(true);
      setIsEditPaymentMethod(true);
    } else if (isEditingPaymentMethod) {
      setMethodChanged(false);
      setIsEditPaymentMethod(false);
    }

    setPaymentFormData({
      ...PaymentFormData,
      paymentMethod: selected.paymentMethodsID,
      isOverviewPage: false,
    });

    setSelectedMethod(methodId);
    navigate(`/${uuid}/${methodId}`);
  };

  return (
    <div className="payment-selector" data-testid="payment-selector-container">
      <div className="page-heading d-none d-lg-block">
        <span>
          <span className="page-heading-span3">
            <FormattedMessage
              id={CONSTANTS.PAYMENT_METHODS.PAGE_HEADING}
              defaultMessage="Payment-link"
            />
            <br />
          </span>
          <span className="page-heading-span4">
            <FormattedMessage
              id={CONSTANTS.PAYMENT_METHODS.SUB_PAGE_HEADING}
              defaultMessage="Checkout"
            />
          </span>
        </span>
      </div>
      {pblResponseData?.error && <GeneralErrorWidget customMessage={pblResponseData?.error} />}
      <div className="headline">
        <div className="frame-74371">
          <div className="labelPaymentHeading" style={{ display: isOverviewPage ? 'inline-flex' : 'block' }}>
            <FormattedMessage
              id={CONSTANTS.PAYMENT_METHODS.SECTION_HEADING}
              defaultMessage="Payment methods"
            />
            {!!isOverviewPage && (
              <span className="text">
                {!isMethodchanged && (
                  !isEditingPaymentMethod ? (
                    <a
                      href="#"
                      onClick={() => setIsEditPaymentMethod(true)}
                      className="text-decoration-underline"
                      style={{ fontSize: '1rem', marginLeft: 8 }}
                    >
                      <FormattedMessage id={CONSTANTS.FORM_EDIT.EDIT_LINK_TEXT} defaultMessage="Edit" />
                    </a>
                  ) : (
                    <a
                      href="#"
                      onClick={() => setIsEditPaymentMethod(false)}
                      className="text-decoration-underline"
                      style={{ fontSize: '1rem', marginLeft: 8 }}
                    >
                      <FormattedMessage id={CONSTANTS.FORM_EDIT.SAVE_BUTTON_TEXT} defaultMessage="Save" />
                    </a>
                  )
                )}
              </span>
            )}
          </div>
        </div>
        {!PaymentFormData.isOverviewPage && <div className="volte-medium">
          <FormattedMessage
            id={CONSTANTS.PAYMENT_METHODS.SUB_SECTION_HEADING}
            defaultMessage="Please select a payment method"
          />
        </div>}
      </div>

      <div className="payment-methods2" data-testid="payment-options-container">
        {dynamicPaymentOptions.map((method) => (
          <div
            key={method.id}
            data-testid={`payment-option-${method.id}`}
            className={`payment-option ${selectedMethod === method.id ? "active" : ""}`}
            onClick={() => handleMethodSelect(method.id)}
            role="radio"
            aria-checked={selectedMethod === method.id}
          >
            <div className="content">
              <div className="custom-radio">
                <div className="bg">
                  <input
                    type="radio"
                    key={method.id}
                    name="payment-method"
                    checked={selectedMethod === method.id}
                    className="custom-radio"
                    readOnly
                  />
                </div>
              </div>
              <div className="text">
                <div className="frame-74434">
                  <div className="s-money-transfer">
                    <div className="div">
                      <img
                        src={iconMapping[method.paymentMethodsID]}
                        alt={`${method.label} icon`}
                        id={`${method.id}-icon`}
                        className={method.id === "creditCard" ? "payment-icon credit-card" : "payment-icon"}
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="volte-medium"
                  data-testid={`payment-label-${method.id}`}
                >
                  <FormattedMessage id={method.translationKey} defaultMessage={method.label} />
                </div>
                <div>
                  {method.id === "creditCard" ?
                    schemeLogos.map((schemeLogo: SchemeLogo, index) => (
                      <img
                        src={Object.values(schemeLogo)[0] as string}
                        alt={`${method.label} icon`}
                        className={Object.keys(schemeLogo)[0] === "5002" ? " click-to-pay-logo" : "scheme-logo"}
                        key={index}
                      />
                    )) : null
                  }
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentMethods;
